import React, { useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import 'react-csv-importer/dist/index.css';

import {
    Box,
    Button,
    CircularProgress,
    Divider,
    Drawer,
    Grid,
    LinearProgress,
    Modal,
    Stack,
    Typography,
    Alert,
    IconButton,
    Paper,
    Stepper,
    Step,
    StepLabel,
    Card,
    CardContent,
    Chip,
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { Send, CheckCircle, Close as CloseIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';

import { Importer, ImporterField } from 'react-csv-importer';
import LeadGenImportHistory from './LeadGenImportHistory';
import MainCard from 'ui-component/cards/MainCard';
import SendEmailToStand from '../../Components/SendEmailToStand';

import { GET_ME } from 'graphql/queries';

const BATCH_IMPORT_STANDS = gql`
    mutation BatchImportStands($eventId: String!, $jsonData: String!) {
        batchImportStands(eventId: $eventId, jsonData: $jsonData)
    }
`;

// Import history will use the same ImportedHistory component with role='Exhibitors'

// Required fields for stand import (id is auto-generated, not required)
const REQUIRED_FIELDS = ['company', 'email', 'password', 'allowed_no_of_staff'];
const OPTIONAL_FIELDS = ['id', 'name', 'parentId', 'scan_name', 'sponsorId', 'stallNo', 'link_url'];

// User-friendly error messages
const getUserFriendlyError = error => {
    const errorMap = {
        'Missing required field: company': 'Company name is required',
        'Missing required field: email': 'Email address is required',
        'Missing required field: password': 'Password is required',
        'Missing required field: allowed_no_of_staff': 'Allowed number of staff is required',
        'Invalid email format': 'Please provide a valid email address',
        'Duplicate ID': 'This ID already exists. Please use a unique ID',
        'Duplicate email': 'This email already exists. Please use a unique email',
        'Invalid staff count': 'Allowed number of staff must be a valid number',
        'Bad characters detected': 'Invalid characters found in data. Please check and remove special characters',
        'Unknown error occurred': 'An unexpected error occurred. Please try again or contact support',
    };
    return errorMap[error] || error;
};

export default function LeadGenImporter() {
    const { eventId } = useParams();
    const { data, loading, error } = useQuery(GET_ME);

    if (loading) {
        return <LinearProgress />;
    }
    if (error) {
        return <Typography>{error?.message}</Typography>;
    }

    if (!data?.me?.permissions?.includes('admin') && !data?.me?.permissions?.includes('superadmin')) {
        return <div>Access denied</div>;
    }

    return <MyLeadGenImporter me={data} />;
}

function MyLeadGenImporter({ me }) {
    const { eventId } = useParams();
    const navigate = useNavigate();
    const [uploadResult, setUploadResult] = useState(null);
    const [showResultModal, setShowResultModal] = useState(false);
    const [showEmailModal, setShowEmailModal] = useState(false);
    const [emailStep, setEmailStep] = useState(0); // 0: ask, 1: sending, 2: done
    const [selectedStands, setSelectedStands] = useState([]);
    const [sendingEmails, setSendingEmails] = useState(false);
    const [emailProgress, setEmailProgress] = useState({ sent: 0, total: 0 });
    const [drawerOpen, setDrawerOpen] = useState(false);

    const [doImport, { loading: uploading }] = useMutation(BATCH_IMPORT_STANDS, {
        onError: e => {
            console.error('Import error:', e);
            setUploadResult({
                success: false,
                message: e.message || 'Failed to import data',
                detailedReport: {
                    summary: {
                        totalRows: 0,
                        successfulInsertions: 0,
                        failedInsertions: 0,
                        updatedRows: 0,
                    },
                    failedRows: [],
                    successfulRows: [],
                    updatedRows: [],
                },
            });
            setShowResultModal(true);
        },
        onCompleted: e => {
            try {
                const result = JSON.parse(e?.batchImportStands);
                const report = result.detailedReport || result;
                setUploadResult({
                    success: report.summary.successfulInsertions > 0,
                    message: result.message || 'Import completed',
                    detailedReport: report,
                });
                setShowResultModal(true);
            } catch (error) {
                console.error('Error parsing import result:', error);
                setUploadResult({
                    success: false,
                    message: 'Error processing import result',
                    detailedReport: {
                        summary: {
                            totalRows: 0,
                            successfulInsertions: 0,
                            failedInsertions: 0,
                            updatedRows: 0,
                        },
                        failedRows: [],
                        successfulRows: [],
                        updatedRows: [],
                    },
                });
                setShowResultModal(true);
            }
        },
    });

    const generateCSVFile = (fields, data = []) => {
        let header = fields;
        if (data.length > 0 && typeof data[0] === 'object') {
            header = Object.keys(data[0]);
        }
        const csvRows = [];
        csvRows.push(header.join(','));
        if (data.length > 0) {
            data.forEach(row => {
                const values = header.map(field => {
                    return JSON.stringify(row[field] || '');
                });
                csvRows.push(values.join(','));
            });
        }
        const csvString = csvRows.join('\n');
        const blob = new Blob([csvString], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${eventId}-stands-${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const generateSampleData = () => {
        generateCSVFile([...REQUIRED_FIELDS, ...OPTIONAL_FIELDS]);
    };

    const downloadFailedData = () => {
        if (!uploadResult?.detailedReport?.failedRows || uploadResult.detailedReport.failedRows.length === 0) return;
        generateCSVFile(
            ['rowNumber', 'id', 'company', 'email', 'error', 'reason'],
            uploadResult.detailedReport.failedRows
        );
    };

    const downloadSuccessfulData = () => {
        if (!uploadResult?.detailedReport?.successfulRows || uploadResult.detailedReport.successfulRows.length === 0)
            return;
        generateCSVFile(
            ['rowNumber', 'id', 'company', 'email', 'status', 'id'],
            uploadResult.detailedReport.successfulRows
        );
    };

    const handleUpload = async rows => {
        if (!rows || !Array.isArray(rows) || rows.length === 0) {
            return;
        }
        await doImport({
            variables: {
                eventId,
                jsonData: JSON.stringify(rows),
            },
        });
    };

    // Email sending will be handled by SendEmailToStand component

    const successfulCount = uploadResult?.detailedReport?.summary?.successfulInsertions || 0;
    const failedCount = uploadResult?.detailedReport?.summary?.failedInsertions || 0;
    const updatedCount = uploadResult?.detailedReport?.summary?.updatedRows || 0;

    return (
        <Box>
            <MainCard
                title='Import Lead Generation Stands'
                secondary={
                    <Button
                        variant='outlined'
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate(`/${eventId}/leadgeneration`)}
                    >
                        Back to Lead Generation
                    </Button>
                }
            >
                {/* Helper Information Section */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant='h6' gutterBottom>
                        How to Import Stands
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant='subtitle1' gutterBottom>
                                        📋 Step 1: Prepare Your Data
                                    </Typography>
                                    <Typography variant='body2' paragraph>
                                        Ensure your CSV file includes all required fields: Company, Email, Password, and
                                        Allowed Number of Staff. Optional fields include ID (auto-generated if not
                                        provided), Name, Parent ID, Scan Name, Sponsor ID, Stall Number, and Link URL.
                                    </Typography>
                                    <Typography variant='subtitle1' gutterBottom>
                                        📤 Step 2: Upload File
                                    </Typography>
                                    <Typography variant='body2' paragraph>
                                        Use the upload area below to import your CSV or Excel file. The system will
                                        automatically validate and process your data.
                                    </Typography>
                                    <Typography variant='subtitle1' gutterBottom>
                                        ✨ Step 3: Review Results
                                    </Typography>
                                    <Typography variant='body2'>
                                        After import, review the results and download any failed data for correction.
                                        You can also send confirmation emails to successfully imported stands.
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent>
                                    <Typography variant='subtitle1' gutterBottom>
                                        🎯 Required Fields
                                    </Typography>
                                    <Box sx={{ mb: 2 }}>
                                        {REQUIRED_FIELDS.map(field => (
                                            <Chip
                                                key={field}
                                                label={field}
                                                color='error'
                                                size='small'
                                                sx={{ mr: 0.5, mb: 0.5 }}
                                            />
                                        ))}
                                    </Box>
                                    <Typography variant='subtitle1' gutterBottom>
                                        📝 Optional Fields
                                    </Typography>
                                    <Box>
                                        {OPTIONAL_FIELDS.map(field => (
                                            <Chip
                                                key={field}
                                                label={field}
                                                color='default'
                                                size='small'
                                                sx={{ mr: 0.5, mb: 0.5 }}
                                            />
                                        ))}
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>

                {/* Sample Data Download */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant='h6' gutterBottom>
                        Download Sample Data
                    </Typography>
                    <Button variant='contained' onClick={generateSampleData}>
                        Download Sample CSV
                    </Button>
                    <Typography variant='body2' color='textSecondary' sx={{ mt: 1 }}>
                        Use this sample file as a template for your data upload.
                    </Typography>
                </Box>

                {/* Upload Area */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant='h6' gutterBottom>
                        Upload Stands Data
                    </Typography>
                    <Box
                        sx={{
                            border: '2px dashed #ccc',
                            borderRadius: 2,
                            p: 3,
                            textAlign: 'center',
                            bgcolor: '#fafafa',
                        }}
                    >
                        <Importer dataHandler={handleUpload} defaultNoHeader={false} restartable={true}>
                            {[...REQUIRED_FIELDS, ...OPTIONAL_FIELDS].map((field, index) => (
                                <ImporterField
                                    key={index}
                                    name={field}
                                    label={field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                    optional={!REQUIRED_FIELDS.includes(field)}
                                />
                            ))}
                        </Importer>
                    </Box>
                </Box>

                {/* Upload Status */}
                {uploading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <CircularProgress size={20} />
                        <Typography>Importing data...</Typography>
                    </Box>
                )}

                {/* Action Buttons */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Button
                        variant='outlined'
                        onClick={() => setDrawerOpen(true)}
                        disabled={!me?.me?.permissions?.includes('importhistory')}
                    >
                        See Import History
                    </Button>
                </Box>
            </MainCard>

            {/* Upload Result Modal */}
            <Modal open={showResultModal} onClose={() => setShowResultModal(false)}>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '90%',
                        maxWidth: 800,
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: 24,
                        p: 4,
                        maxHeight: '90vh',
                        overflow: 'auto',
                    }}
                >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant='h5'>Import Results</Typography>
                        <IconButton onClick={() => setShowResultModal(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    {uploadResult && (
                        <>
                            <Alert severity={uploadResult.success ? 'success' : 'error'} sx={{ mb: 2 }}>
                                <Typography variant='body1' sx={{ fontWeight: 'bold' }}>
                                    {uploadResult.message}
                                </Typography>
                            </Alert>

                            {/* Summary Cards */}
                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                <Grid item xs={12} sm={4}>
                                    <Paper
                                        sx={{
                                            p: 2,
                                            bgcolor: 'info.light',
                                            borderRadius: 1,
                                            textAlign: 'center',
                                        }}
                                    >
                                        <Typography variant='h4' color='white'>
                                            {uploadResult.detailedReport?.summary?.totalRows || 0}
                                        </Typography>
                                        <Typography variant='body2' color='white'>
                                            Total Rows
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Paper
                                        sx={{
                                            p: 2,
                                            bgcolor: 'success.light',
                                            borderRadius: 1,
                                            textAlign: 'center',
                                        }}
                                    >
                                        <Typography variant='h4' color='white'>
                                            {successfulCount}
                                        </Typography>
                                        <Typography variant='body2' color='white'>
                                            Successful
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <Paper
                                        sx={{
                                            p: 2,
                                            bgcolor: 'error.light',
                                            borderRadius: 1,
                                            textAlign: 'center',
                                        }}
                                    >
                                        <Typography variant='h4' color='white'>
                                            {failedCount}
                                        </Typography>
                                        <Typography variant='body2' color='white'>
                                            Failed
                                        </Typography>
                                    </Paper>
                                </Grid>
                            </Grid>

                            {/* Download Buttons */}
                            <Box sx={{ mb: 3 }}>
                                <Typography variant='subtitle1' gutterBottom>
                                    Download Data
                                </Typography>
                                <Stack direction='row' spacing={2} flexWrap='wrap' gap={2}>
                                    {successfulCount > 0 && (
                                        <Button variant='outlined' color='success' onClick={downloadSuccessfulData}>
                                            Download Successful ({successfulCount})
                                        </Button>
                                    )}
                                    {failedCount > 0 && (
                                        <Button variant='outlined' color='error' onClick={downloadFailedData}>
                                            Download Failed ({failedCount})
                                        </Button>
                                    )}
                                </Stack>
                            </Box>

                            {/* Failed Data Details */}
                            {failedCount > 0 && uploadResult.detailedReport?.failedRows && (
                                <Box sx={{ mb: 3 }}>
                                    <Typography variant='subtitle1' gutterBottom>
                                        Failed Rows Details
                                    </Typography>
                                    <Box
                                        sx={{
                                            maxHeight: 300,
                                            overflow: 'auto',
                                            border: '1px solid #ddd',
                                            borderRadius: 1,
                                        }}
                                    >
                                        {uploadResult.detailedReport.failedRows.slice(0, 10).map((row, index) => (
                                            <Box
                                                key={index}
                                                sx={{
                                                    p: 2,
                                                    borderBottom: '1px solid #eee',
                                                    '&:last-child': { borderBottom: 'none' },
                                                }}
                                            >
                                                <Typography variant='body2' color='error' gutterBottom>
                                                    <strong>Row {row.rowNumber}:</strong>{' '}
                                                    {getUserFriendlyError(row.error || row.reason)}
                                                </Typography>
                                                {row.id && (
                                                    <Typography variant='caption' color='textSecondary'>
                                                        ID: {row.id} | Company: {row.company || 'N/A'} | Email:{' '}
                                                        {row.email || 'N/A'}
                                                    </Typography>
                                                )}
                                            </Box>
                                        ))}
                                        {uploadResult.detailedReport.failedRows.length > 10 && (
                                            <Typography
                                                variant='body2'
                                                color='textSecondary'
                                                sx={{ p: 2, textAlign: 'center' }}
                                            >
                                                ... and {uploadResult.detailedReport.failedRows.length - 10} more failed
                                                rows
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            )}

                            {/* Email Sending Section */}
                            {successfulCount > 0 && emailStep === 0 && (
                                <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                                    <Typography variant='h6' gutterBottom>
                                        Send Confirmation Emails?
                                    </Typography>
                                    <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                                        You can send confirmation emails to {successfulCount} successfully imported
                                        stand(s). You'll be able to select a template and customize the email before
                                        sending.
                                    </Typography>
                                    <Stack direction='row' spacing={2}>
                                        <Button
                                            variant='contained'
                                            color='primary'
                                            onClick={() => {
                                                setSelectedStands(uploadResult.detailedReport.successfulRows);
                                                setCurrentStandIndex(0);
                                                setShowEmailModal(true);
                                            }}
                                        >
                                            Yes, Send Emails ({successfulCount})
                                        </Button>
                                        <Button variant='outlined' onClick={() => setShowResultModal(false)}>
                                            No, Skip
                                        </Button>
                                    </Stack>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            </Modal>

            {/* Email Sending Modal - Show for each stand individually */}
            {showEmailModal && selectedStands.length > 0 && currentStandIndex < selectedStands.length && (
                <SendEmailToStand
                    open={showEmailModal}
                    onClose={() => {
                        if (currentStandIndex < selectedStands.length - 1) {
                            // Move to next stand
                            setCurrentStandIndex(currentStandIndex + 1);
                        } else {
                            // All stands processed
                            setShowEmailModal(false);
                            setCurrentStandIndex(0);
                            setSelectedStands([]);
                            setShowResultModal(false);
                        }
                    }}
                    stand={selectedStands[currentStandIndex]}
                />
            )}

            {/* History Drawer */}
            <Drawer
                anchor='right'
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
                PaperProps={{ sx: { width: '50%' } }}
            >
                <Box p={2}>
                    <Typography textAlign='center' variant='h6'>
                        Import History
                    </Typography>
                    <Divider />
                    <LeadGenImportHistory eventId={eventId} />
                </Box>
            </Drawer>
        </Box>
    );
}
